import { isNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { createEntity, subscribeByID, updateEntity } from '@adornis/baseql/operations/mongo.js';
import { type NarrowedEntity, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { EmptyState } from '@adornis/components/client/EmptyState.js';
import { alertDialog, confirmDialog, guardDialog, promptDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { TextareaInput } from '@adornis/formfield/fields/textarea-input.js';
import { ToggleInput } from '@adornis/formfield/fields/toggle-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useEffect, useMemo, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { formatCalendarDay, toCalendarDay, todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import { html, nothing } from 'lit';
import { filter, firstValueFrom, type Observable, of, timeout } from 'rxjs';
import { SgSnackbar } from '../../../client/components/ui/sg-snackbar.js';
import { useMartinusABAC } from '../../../permissions/client/useMartinusAbac.js';
import { Role } from '../../../permissions/roles.js';
import type { UserWithPermissionFields } from '../../../permissions/UserWithPermissionFields.js';
import { TicketAPI } from '../../../ticket/api/TicketAPI.js';
import { UserAPI } from '../../../users/api/UserAPI.js';
import { Person } from '../../../users/entities/person.js';
import type { UserOnUserPayload } from '../../../users/UserPolicy.js';
import { changesContainPaths } from '../../api/changesContainPaths.js';
import { ContractAPI, ContractApplyOptions } from '../../api/ContractAPI.js';
import {
  changeEndDateError,
  changeStartDateError,
  lastActiveDayOfEndedContract,
} from '../../api/contractTimeframes.js';
import { generateContractSupplementFromChangedEntity } from '../../api/generateContractSupplementFromChangedEntity.js';
import { getDateWithMostActiveSections } from '../../api/getMinAndMaxDatesOfSupplement.js';
import { FULL_TIME_WORKING_HOURS } from '../../constants/fullTimeWorkingHours.js';
import { ContractsActions } from '../../ContractsPolicy.js';
import { AppliedContract } from '../../entities/AppliedContract.js';
import { Contract } from '../../entities/Contract.js';
import {
  type ContractMetaInformation,
  ContractMetaInformationComment,
  ContractMetaInformationCommentType,
} from '../../entities/ContractMetaInformation.js';
import { ContractSupplement } from '../../entities/ContractSupplement.js';
import { AGEingruppierung } from '../../entities/vertrag-sektionen/vertrag-eingruppierung/AGEingruppierung.js';
import { AGVertragsInfos } from '../../entities/vertrag-sektionen/vertrag-eingruppierung/AGvertragsinfos.js';
import { KostenstelleProzentsatz } from '../../entities/vertrag-sektionen/vertrag-eingruppierung/KostenstelleProzentsatz.js';
import { MAVStatus } from '../../enums/MAVStatus.js';
import { checkIfChangeIsMadeOnTemporarySupplement } from '../contract-temporary-supplement-enrichment.js';
import { showContractDocumentManagerDialog } from '../dialogs/ContractDocumentManagerDialog.js';
import { showSupplementCreationDialog } from '../dialogs/SupplementCreationDialog.js';
import { updateContractTaetigkeitsschluessel } from '../updateContractTaetigkeitsschluessel.js';
import { ContractEdit } from './contract-edit/ContractEdit.js';
import { ContractEditSimple, type SupplementIntervallDates } from './contract-edit/ContractEditSimple.js';
import { buildContractValidator, canEditContractDatesFreely } from './contract-edit/contractValidators.js';
import {
  ContractProcessComments,
  isInitialContractProcessFinished,
  isInitialProcessMAVReady,
} from './ContractProcessComments.js';
import { ContractTimeline, type TimelineContract, type TimelineSupplement } from './ContractTimeline.js';
import { SupplementEdit, type SupplementEditSupplement } from './SupplementEdit.js';

const CONTRACTS_SELECTION = {
  _id: 1,
  justification: 1,
  createdAt: 1,
  agInfos: {
    einstellungsdatum: 1,
    gekuendigtZum: 1,
    unterzeichnungsdatum: 1,
  },
  supplements: {
    _id: 1,
    justification: 1,
    signedAt: 1,
    subNachtragsnummer: 1,
    nachtragsNummer: 1,
    startDateAG1: 1,
    startDateAG2: 1,
    endDateAG1: 1,
    endDateAG2: 1,
    startDateGeneral: 1,
    endDateGeneral: 1,
    changes: { path: 1, type: 1, value: 1 },
    paragraph: 1,
    automaticallyAssignedEndDateOfContract: 1,
  },
} as const;

type ContractListEntry = NarrowedEntity<Contract, typeof CONTRACTS_SELECTION>;

type ContractMetaLite = NarrowedEntity<
  ContractMetaInformation,
  { mavStatus: 1; comments: { _id: 1; oldProcess: 1; linkedSupplementID: 1 } }
>;

/** loads the current meta information of a contract for the process handling */
const fetchContractMeta = async (contractID: string) =>
  (await firstValueFrom(
    ContractAPI.subscribeMeta(contractID)({
      mavStatus: 1,
      comments: { _id: 1, oldProcess: 1, linkedSupplementID: 1 },
    }).pipe(
      // the meta is created lazily by the subscribe resolver: for a fresh contract the first emission is
      // always null and the created doc only arrives on the next one, so we must wait for the first non-null
      filter(meta => meta !== null),
      timeout({ first: 10_000 }),
    ),
  )) as Maybe<ContractMetaInformation>;

interface ContractsViewProps {
  personID: Maybe<string>;
}

/**
 * Root view of the contracts tab of a person. Handles the selection of contracts, supplements and the point in time,
 * generates supplements from changes and runs the whole MAV process.
 */
export const ContractsView = stateful((props: ContractsViewProps) => {
  const user = useObservable(() => currentUser<Person>(), []);

  const { value: person } = useQuery(
    () =>
      subscribeByID(
        Person,
        props.personID ?? '',
      )({
        _id: 1,
        roles: { name: 1, contexts: 1, permissions: 1 },
        profilDaten: {
          stammdaten: {
            geburtsDatum: 1,
          },
        },
        mitarbeiterDaten: {
          anSozialVersicherung: {
            versicherungsStatus: 1,
          },
        },
      }),
    [props.personID],
  );

  const { value: contracts, loading: contractsLoading } = useQuery<Maybe<ContractListEntry[]>>(
    () =>
      person?._id
        ? (ContractAPI.subscribeByUserID(person._id)(CONTRACTS_SELECTION) as unknown as Observable<ContractListEntry[]>)
        : of(null),
    [person?._id],
  );

  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(todayAsCalendarDay());
  const [selectedSupplement, setSelectedSupplement] = useState<ContractSupplement | null>(null);
  const [isEditingSupplement, setIsEditingSupplement] = useState(false);
  const [expertView, setExpertView] = useState(false);
  // gates the permissions until the view is ready. Kept separate from `refreshing` so that recalculating the
  // applied contract does not rebuild the whole form and make every field flicker.
  const [internalLoading, setInternalLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // guards against an older applied-contract request overwriting the result of a newer one
  const requestToken = useMemo(() => [{ current: 0 }], []);
  const [saveInProcess, setSaveInProcess] = useState(false);
  const [readonly, setReadonly] = useState(false);
  const [appliedContract, setAppliedContract] = useState<AppliedContract | null>(null);
  const [stableContractObject, setStableContractObject] = useState<Maybe<EntityData<AppliedContract>>>(null);
  const [supplementFromChange, setSupplementFromChange] = useState<ContractSupplement | null>(null);
  const [overrideShowEmptyNewSupplement, setOverrideShowEmptyNewSupplement] = useState<ContractSupplement | null>(null);
  const [kuendigungSetDate, setKuendigungSetDate] = useState<Date | null>(null);
  const [alertedUserForMissingSupplementStart, setAlertedUserForMissingSupplementStart] = useState(false);
  // simple mode keeps the supplement intervall dates outside of the contract entity
  const [supplementDates, setSupplementDates] = useState<SupplementIntervallDates>(
    {
      startDateAG1: selectedSupplement?.startDateAG1,
      endDateAG1: selectedSupplement?.endDateAG1,
      startDateAG2: selectedSupplement?.startDateAG2,
      endDateAG2: selectedSupplement?.endDateAG2,
    },
    [selectedSupplement?._id],
  );

  const { value: contractMeta } = useQuery<Maybe<ContractMetaLite>>(
    () =>
      selectedContract?._id
        ? (ContractAPI.subscribeMeta(selectedContract._id)({
            mavStatus: 1,
            comments: { _id: 1, oldProcess: 1, linkedSupplementID: 1 },
          }) as unknown as Observable<Maybe<ContractMetaLite>>)
        : of(null),
    [selectedContract?._id],
  );

  const simpleMode = !!user?.roles.find(r => r.name === Role.SCHULLEITER || r.name === Role.MAV);

  const { can: canEditContract } = useMartinusABAC<UserOnUserPayload>(
    { action: ContractsActions.EDIT_CONTRACT, viewedUser: person as Maybe<UserWithPermissionFields> },
    [person?._id],
  );
  const { can: canEditSupplement } = useMartinusABAC<UserOnUserPayload>(
    { action: ContractsActions.EDIT_SUPPLEMENT, viewedUser: person as Maybe<UserWithPermissionFields> },
    [person?._id],
  );

  const hasEditPermission = canEditContract;

  const initialProcessFinished = isInitialContractProcessFinished(contractMeta);

  // the simple view locks editing while a MAV process is running
  const canEditSimple = (() => {
    const isSchulleiter = !!user?.roles.find(r => r.name === Role.SCHULLEITER);
    const isSchulleiterAndNewContract = isSchulleiter && isNullish(contractMeta?.mavStatus);
    const mavIsActive =
      contractMeta?.mavStatus === MAVStatus.MAV_RUECKFRAGE || contractMeta?.mavStatus === MAVStatus.MAV_BETEILIGEN;
    const shouldBePossibleToEdit =
      isSchulleiterAndNewContract || !isSchulleiter || (!mavIsActive && initialProcessFinished);
    return shouldBePossibleToEdit && hasEditPermission;
  })();

  // viewing a past day is read only, because a change there would silently rewrite history. The
  // Personalabteilung and admins maintain that history themselves and have to be able to correct it.
  const readonlyForUser = readonly && !canEditContractDatesFreely(user);

  const effectiveEditPermission = internalLoading || readonlyForUser ? false : hasEditPermission;
  const formCanEdit = expertView ? effectiveEditPermission : effectiveEditPermission && canEditSimple;

  const placeholderContract = useMemo(() => [new AppliedContract({ userID: '' })], []);

  // the dates the contract was loaded with. the day rules of the simple mode only apply to dates the user
  // actually enters, so a contract that already holds other days does not block saving an unrelated change
  const [loadedContractDates] = useState(
    {
      einstellungsdatum: appliedContract?.agInfos?.einstellungsdatum,
      gekuendigtZum: appliedContract?.agInfos?.gekuendigtZum,
    },
    [appliedContract],
  );

  const form = useForm<EntityData<AppliedContract>>(
    (appliedContract ?? placeholderContract) as EntityData<AppliedContract>,
    {
      validator: buildContractValidator(() => person, !expertView, {
        loaded: loadedContractDates,
        canEditDatesFreely: canEditContractDatesFreely(user),
      }),
      enabledFields: formCanEdit ? undefined : {},
      onValuePicked: () => void recomputeSupplementFromChange(),
    },
    [appliedContract, expertView, formCanEdit],
  );

  /**
   * Fetches the state of the contract for the given day.
   *
   * The editing flags are passed in explicitly instead of being read from `isEditingSupplement`: the state
   * setter has not been applied yet while the toggle handler runs, so reading it from the closure would apply
   * the previous value and fetch the wrong state.
   */
  const recalculateAppliedContract = async (
    contract: Contract,
    date: Date,
    options?: { includeDrafts?: boolean; editingSupplement?: boolean },
  ) => {
    if (!contract._id) {
      const applied = new AppliedContract({ ...contract.toObject() });
      setAppliedContract(applied);
      setStableContractObject(applied.toJSON());
      setSupplementFromChange(null);
      return;
    }

    const editingSupplement = options?.editingSupplement ?? isEditingSupplement;
    const token = ++requestToken.current;
    setRefreshing(true);

    const selectedSublementIndex = selectedContract?.supplements?.findIndex(s => s._id === selectedSupplement?._id);

    const applied = await ContractAPI.getApplied(
      contract._id,
      new ContractApplyOptions({
        applyDate: date,
        includeDrafts: options?.includeDrafts ?? false,
        overrideActiveCheck: false,
        subIndexSelected: editingSupplement ? selectedSublementIndex : undefined,
      }),
    )({
      ...selectionSet(() => AppliedContract, 4),
      agEingruppierung1: {
        ...selectionSet(() => AGEingruppierung, 3),
        agVertragsInfos: {
          ...selectionSet(() => AGVertragsInfos, 1),
          kostenstelleProzentsaetze: {
            _id: 1,
            kostenstelleID: 1,
            prozentsatz: 1,
            kostenstelle: {
              name: 1,
              parentIDs: 1,
              school: {
                name: 1,
              },
            },
          },
        },
      },
      agEingruppierung2: {
        ...selectionSet(() => AGEingruppierung, 3),
        agVertragsInfos: {
          ...selectionSet(() => AGVertragsInfos, 1),
          kostenstelleProzentsaetze: {
            _id: 1,
            kostenstelleID: 1,
            prozentsatz: 1,
            kostenstelle: {
              name: 1,
              parentIDs: 1,
              school: {
                name: 1,
              },
            },
          },
        },
      },
    });

    // a newer request was started in the meantime, its result is the one the user is waiting for
    if (token !== requestToken.current) return;

    setAppliedContract(applied as AppliedContract);
    setStableContractObject((applied as AppliedContract)?.toJSON() ?? null);
    setSupplementFromChange(null);
    setRefreshing(false);
  };

  /**
   * derives the supplement from the changes made on the applied contract compared to the stable state.
   *
   * Returns the freshly derived supplement so callers that pick a value and save in one run do not have to wait
   * for the state of the next render.
   */
  const recomputeSupplementFromChange = async (): Promise<Maybe<ContractSupplement>> => {
    if (!appliedContract || !stableContractObject) return supplementFromChange;

    /**
     * are taetigkeitenschluessel relevant fields changed?
     * if so we need to recalculate the schluessel and write it to the contract so the supplement is generated correctly
     */
    await updateContractTaetigkeitsschluessel(appliedContract, stableContractObject);

    const supplement = await generateContractSupplementFromChangedEntity(appliedContract, stableContractObject);

    /**
     * The generated supplement is a fresh entity with a fresh id on every keystroke. Components below key their
     * local state on that id, so without carrying the previous one over, the supplement panel would reset its
     * edit mode and its date inputs while the user is typing in the contract form.
     */
    const previousID = overrideShowEmptyNewSupplement?._id ?? supplementFromChange?._id;
    if (previousID) supplement._id = previousID;

    // if the user override the logic and created a supplement with empty changes we just strip its values to our new one
    if (overrideShowEmptyNewSupplement) {
      supplement.paragraph = overrideShowEmptyNewSupplement.paragraph;
      supplement.startDateGeneral = overrideShowEmptyNewSupplement.startDateGeneral;
      supplement.endDateGeneral = overrideShowEmptyNewSupplement.endDateGeneral;
      supplement.signedAt = overrideShowEmptyNewSupplement.signedAt;
      supplement.startDateAG1 = overrideShowEmptyNewSupplement.startDateAG1;
      supplement.endDateAG1 = overrideShowEmptyNewSupplement.endDateAG1;
      supplement.startDateAG2 = overrideShowEmptyNewSupplement.startDateAG2;
      supplement.endDateAG2 = overrideShowEmptyNewSupplement.endDateAG2;
      if (supplement.changes?.length) setOverrideShowEmptyNewSupplement(null);
    } else {
      // add the user made changes to the new supplement
      supplement.paragraph = supplementFromChange?.paragraph;
      supplement.startDateGeneral = supplementFromChange?.startDateGeneral;
      supplement.endDateGeneral = supplementFromChange?.endDateGeneral;
      supplement.signedAt = supplementFromChange?.signedAt;
      supplement.startDateAG1 = supplementFromChange?.startDateAG1;
      supplement.endDateAG1 = supplementFromChange?.endDateAG1;
      supplement.startDateAG2 = supplementFromChange?.startDateAG2;
      supplement.endDateAG2 = supplementFromChange?.endDateAG2;
    }

    const derived = supplement.changes?.length ? supplement : null;
    setSupplementFromChange(derived);
    return derived;
  };

  /**
   * The contract is passed explicitly by the initial selection: it selects and jumps in one run, and the
   * `selectedContract` of this render is still the previous one until the state update is applied.
   */
  const setApplyDateToSupplementStart = async (
    supplement: TimelineSupplement | ContractSupplement,
    contract: Maybe<Contract> = selectedContract,
  ) => {
    const { start } = getDateWithMostActiveSections(supplement);
    if (!start) {
      if (!alertedUserForMissingSupplementStart) {
        setAlertedUserForMissingSupplementStart(true);
        await alertDialog(
          'Vertragsdatum, sollte auf das Startdatum von Nachtrag ' +
            supplement.nachtragsNummer +
            (supplement.subNachtragsnummer ? `.${supplement.subNachtragsnummer}` : '') +
            ' gesetzt werden. Dieser hat allerdings kein Startdatum.',
        );
      }
      return;
    }
    if (start.getTime() === selectedDate.getTime()) return;
    setSelectedDate(start);

    if (!contract) return;

    await recalculateAppliedContract(contract, start, { includeDrafts: true });
  };

  // initial contract / supplement selection as soon as the contracts arrive
  useEffect(() => {
    if (!contracts) return;

    void (async () => {
      let currentSelected = selectedContract;
      // none of the state set in this run is applied yet, so the contract and the day are tracked locally
      let applyDate = selectedDate;
      if (!currentSelected) {
        // if no contract check the query parameter, the user might have followed a link to a contract
        const urlSearchParams = new URLSearchParams(window.location.search);
        const contractID = urlSearchParams.get('contractID');
        const contract = contracts.find(c => c._id === (contractID ?? 'NO_ID'));

        if (contract) currentSelected = contract as unknown as Contract;
        else if (contracts.length) {
          // if no contract is selected, select the last contract
          currentSelected = (contracts.at(-1) ?? null) as unknown as Contract | null;
        }
        setSelectedContract(currentSelected);

        // after selecting a contract, check if the user followed a link to a supplement
        const supplementID = urlSearchParams.get('supplementID');
        if (supplementID) {
          const supplement = currentSelected?.supplements?.find(s => s._id === supplementID);
          if (supplement) {
            setSelectedSupplement(supplement as unknown as ContractSupplement);
            await setApplyDateToSupplementStart(supplement as unknown as ContractSupplement, currentSelected);
            return;
          }
        }
      }

      // an ended contract opens on the last day it was actually active, a running one on today
      const lastActiveDay = currentSelected ? lastActiveDayOfEndedContract(currentSelected) : null;
      if (lastActiveDay) {
        applyDate = lastActiveDay;
        setSelectedDate(applyDate);
        setReadonly(toCalendarDay(applyDate).getTime() !== todayAsCalendarDay().getTime());
      }

      if (currentSelected) void recalculateAppliedContract(currentSelected, applyDate);
    })();
  }, [contracts]);

  // Schulleitung / MAV: preselect the last unsigned supplement of a running process
  useEffect(() => {
    if (!user || !contracts) return;

    void (async () => {
      const relevantContract = contracts.at(-1);
      if (relevantContract && user.roles.find(r => r.name === Role.SCHULLEITER || r.name === Role.MAV)) {
        // outside of a guardDialog a fetch timeout must not become an unhandled rejection
        const meta = await fetchContractMeta(relevantContract._id).catch(() => null);
        const currentlyInProcess = meta?.mavStatus !== MAVStatus.MAV_BESTAETIGT;

        // only select the last unsigned supplement if the mav has not accepted it currently. If the mav accepted it, the contract is in process and all changes should be made on a new supplement
        if (!selectedSupplement && currentlyInProcess) {
          const lastUnsignedSupp = relevantContract.supplements?.find(s => !s.signedAt);
          if (lastUnsignedSupp) {
            setSelectedSupplement(lastUnsignedSupp as unknown as ContractSupplement);
            await setApplyDateToSupplementStart(
              lastUnsignedSupp as unknown as ContractSupplement,
              relevantContract as unknown as Contract,
            );
          }
        }
      }
    })();
  }, [user?._id, contracts]);

  // createNew url parameter opens a fresh contract draft
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('createNew') === 'true') {
      urlParams.delete('createNew');

      // Update URL without reloading the page
      const newUrl = `${window.location.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ''}`;
      window.history.replaceState({}, '', newUrl);

      setInternalLoading(false);
      handleNewContract();
    } else {
      setInternalLoading(false);
    }
  }, []);

  const handleNewContract = () => {
    void guardDialog(
      async () => {
        // the owner comes from the route, not from the loaded person: the createNew url parameter opens the
        // draft on mount, where the person subscription has not resolved yet. A contract without userID is
        // rejected by the server as a missing permission, not as missing data
        const newContract = new Contract({ userID: props.personID ?? person?._id });

        // in the simple mode we want to prefill the stunden in arbeitszeit. This is not done via a default value in the orm because it would destroy some exports which create the subdate if it is undefined
        if (simpleMode && newContract.agEingruppierung1?.agArbeitszeit) {
          newContract.agEingruppierung1.agArbeitszeit.stunden = FULL_TIME_WORKING_HOURS;
        }

        setSelectedContract(newContract);
        setSelectedSupplement(null);
        setOverrideShowEmptyNewSupplement(null);

        // in simple mode the person creating the contract is a principal, so the first kostenstelle row is
        // opened right away. Its school is preselected by the field itself from the principal role.
        if (simpleMode) {
          newContract.agEingruppierung1?.agVertragsInfos?.kostenstelleProzentsaetze?.push(
            new KostenstelleProzentsatz({}),
          );
        }

        setSelectedDate(todayAsCalendarDay());
        setReadonly(false);

        await recalculateAppliedContract(newContract, todayAsCalendarDay());
      },
      {
        success: () => {
          void SgSnackbar.show('Neuer Vertrag wurde erfolgreich vorbereitet.');
        },
      },
    );
  };

  const handleKuendigungSet = async (date: Date) => {
    if (!appliedContract?._id) return;
    if (!appliedContract.agInfos) return;
    if (!(await confirmDialog('Wollen Sie den Vertrag kündigen und damit auch alle Nachträge beenden?'))) {
      appliedContract.agInfos.gekuendigtZum = null;
      return;
    }

    setKuendigungSetDate(date);

    const hasOpenJobTicket = await TicketAPI.isOpen(props.personID ?? '')();
    if (
      hasOpenJobTicket &&
      (await confirmDialog(
        'Die Person besitzt noch ein offenes Jobticket. Wollen Sie dieses mit dem Kündigungsdatum beenden?',
      ))
    ) {
      await TicketAPI.close(props.personID ?? '', date)();
    }

    await handleSave(date);
  };

  const skipMav = async () => {
    await guardDialog(
      async () => {
        if (!selectedContract?._id) throw new Error('Kein Vertrag ausgewählt');

        // get the contract meta so we can write a new process
        const meta = await fetchContractMeta(selectedContract._id);

        if (!meta) throw new Error('Vertrag Metainformationen nicht gefunden. Könnte an einer Racecondition liegen.');

        const comment = await promptDialog('Begründung für das Überspringen.', {
          placeholder: 'Begründung',
        });

        if (
          !(await confirmDialog('Den MAV Prozess wirklich überspringen?', {
            acceptText: 'Überspringen',
            declineText: 'Abbrechen',
            highlightDecline: true,
          }))
        ) {
          return false;
        }

        // in this case the psbs want to override the process
        if (meta.mavStatus !== null && meta.mavStatus !== MAVStatus.MAV_BESTAETIGT) {
          const lastComment = meta.comments?.[meta.comments.length - 1];

          meta.mavStatus = MAVStatus.MAV_BESTAETIGT;
          meta.comments?.push(
            new ContractMetaInformationComment({
              userID: user?._id,
              userRole: Role.PSB,
              type: ContractMetaInformationCommentType.MAV_SKIPPED,
              text: comment,
              linkedSupplementID: lastComment?.linkedSupplementID,
              oldProcess: lastComment?.oldProcess,
            }),
          );

          for (const c of meta.comments ?? []) {
            c.oldProcess = true;
          }

          await meta.save();
          return;
        }

        const connectSupplement =
          !!selectedSupplement &&
          (await confirmDialog(
            'Es ist ein Nachtrag ausgewählt. Soll der MAV Prozess des Nachtrags übersprungen werden?',
            {
              acceptText: 'MAV Prozess des Nachtrags überspringen',
              declineText: 'Nur für den Vertrag überspringen',
            },
          ));

        meta.comments?.push(
          new ContractMetaInformationComment({
            userID: user?._id,
            userRole: Role.PSB,
            type: ContractMetaInformationCommentType.PSB_MANUAL,
            linkedSupplementID: connectSupplement ? selectedSupplement?._id : undefined,
          }),
        );

        meta.comments?.push(
          new ContractMetaInformationComment({
            userID: user?._id,
            userRole: Role.PSB,
            type: ContractMetaInformationCommentType.MAV_SKIPPED,
            text: comment,
            linkedSupplementID: connectSupplement ? selectedSupplement?._id : undefined,
          }),
        );

        meta.mavStatus = MAVStatus.MAV_BESTAETIGT;

        for (const c of meta.comments ?? []) {
          c.oldProcess = true;
        }

        await meta.save();
      },
      {
        success: () => {
          void SgSnackbar.show('MAV Prozess wurde erfolgreich übersprungen.');
        },
      },
    );
  };

  const handleMavBeteiligen = async () => {
    await guardDialog(
      async () => {
        if (!selectedContract?._id) throw new Error('Kein Vertrag ausgewählt');

        // get the contract meta so we can write a new process
        const meta = await fetchContractMeta(selectedContract._id);

        if (!meta) throw new Error('Vertrag Metainformationen nicht gefunden. Könnte an einer Racecondition liegen.');

        if (!appliedContract?.agEingruppierung1?.agVertragsInfos?.kostenstelleProzentsaetze?.at(0)?.kostenstelleID) {
          throw new Error('Es wurde keine primäre Kostenstelle gefunden.');
        }

        if (
          meta.mavStatus !== null &&
          meta.mavStatus !== MAVStatus.MAV_BESTAETIGT &&
          meta.mavStatus !== MAVStatus.MAV_ABGELEHNT
        ) {
          throw new Error('MAV Prozess ist nicht abgeschlossen.');
        }

        const initialFinished = meta.comments?.find(c => !c.linkedSupplementID)?.oldProcess ?? false;

        if (initialFinished && !selectedSupplement) {
          const wantedSupplement = await confirmDialog(
            'Der initale Einstellungsprozess ist abgeschlossen. Möchten Sie den Prozess über einen Nachtrag starten? Dann wählen Sie diesen bitte zuerst aus und starten danach den MAV Prozess.',
            { acceptText: 'MAV Prozess über Nachtrag starten', declineText: 'Nur über den Vertrag beteiligen' },
          );
          if (wantedSupplement) {
            return false;
          }
        }

        const connectSupplement =
          !!selectedSupplement &&
          (await confirmDialog(
            'Es ist ein Nachtrag ausgewählt. Soll dieser Nachtrag mit dem MAV Prozess verbunden werden?',
            {
              acceptText: 'MAV Prozess über Nachtrag starten',
              declineText: 'Nur über den Vertrag beteiligen',
            },
          ));

        const comment = await promptDialog('Kommentar für die MAV kann auch leer bleiben.', {
          placeholder: 'Kommentar',
        });

        if (
          !(await confirmDialog('Die MAV jetzt an den Änderungen beteiligen?', {
            acceptText: 'MAV Beteiligen',
            declineText: 'Abbrechen',
          }))
        ) {
          return false;
        }

        meta.comments?.push(
          new ContractMetaInformationComment({
            userID: user?._id,
            userRole: Role.PSB,
            type: ContractMetaInformationCommentType.PSB_MANUAL,
            linkedSupplementID: connectSupplement ? selectedSupplement?._id : undefined,
            text: comment,
          }),
        );

        meta.mavStatus = MAVStatus.MAV_BETEILIGEN;

        // remove the old flag from all others which do not have a linked supplement. The process is active once more. But only if we have a general process. else the process has its own comments
        if (!connectSupplement) {
          for (const c of meta.comments?.filter(c2 => !c2.linkedSupplementID) ?? []) {
            c.oldProcess = false;
          }
        }

        await meta.save();

        await ContractAPI.notifyChange(
          selectedContract._id,
          Role.MAV,
          'die Personalverwaltung hat Sie an einer Änderung beteiligt.',
          connectSupplement ? selectedSupplement?._id : undefined,
        )();

        return true;
      },
      {
        success: e => {
          if (e) void SgSnackbar.show('MAV beteiligt');
        },
      },
    );
  };

  const saveOrCreateEntityWithoutValidation = async (e: Contract, changeSupplement: Maybe<ContractSupplement>) => {
    await e.commit(true);
    if (!e._id) {
      const _id = await createEntity(e)();
      e._id = _id;
      // the form edits the applied contract, so the entered values only reach the fresh contract as changes
      if (changeSupplement?.changes?.length) await ContractAPI.saveSupplementToBase(_id, changeSupplement)();

      return _id;
    } else {
      await updateEntity(e)();
      return e._id;
    }
  };

  /** asks the schulleitung for a justification and comment for a new contract */
  const askForJustification = () =>
    showDialog<{ justification: string; comment: string }>({
      content: resolve => {
        let justification = '';
        let comment = '';
        return html`
          <div class="dialog-container">
            <div class="flex flex-col gap-2">
              <h3 class="font-semibold">Begründung für den Vertrag</h3>
              ${TextareaInput({
                placeholder: 'Hier können Sie eine eventuelle Begründung für den Vertrag eingeben...',
                onPicked: (value: string | null) => (justification = value ?? ''),
              })}
            </div>

            <hr class="border-base-300" />

            <div class="flex flex-col gap-2">
              <h3 class="font-semibold">Wollen Sie der MAV etwas Zusätzliches mitteilen?</h3>
              ${TextInput({
                placeholder: 'Optionale Informationen',
                placeholderMode: 'hide',
                clearable: true,
                onPicked: (value: string | null) => (comment = value ?? ''),
              })}
            </div>
            <button class="btn btn-primary" @click=${() => resolve({ justification, comment })}>Vertrag anlegen</button>
          </div>
        `;
      },
    });

  const handleSave = async (kuendigungDate: Maybe<Date> = kuendigungSetDate) => {
    if (!person || !appliedContract) return;

    let skipClean = false;
    setSaveInProcess(true);

    // the change set is derived here instead of read from the state: the Kündigung writes the date and saves in one
    // run, so the state of the render this handler was created in still holds the change set from before that date
    const changeSupplement = await recomputeSupplementFromChange();

    // the cleanup tidies up after a save. it reverts the local state and recalculates the contract based on the new changes
    const cleanup = async () => {
      if (!person) {
        setSaveInProcess(false);
        return;
      }
      appliedContract.revert();
      if (selectedContract) await recalculateAppliedContract(selectedContract, selectedDate);
      // employee bookings and roles follow the saved contract on the server, in its hooks
      setOverrideShowEmptyNewSupplement(null);
    };

    // validates the form. On errors the user is informed and (outside of the simple mode) can decide to continue anyway.
    const validateWithConfirm = async (): Promise<boolean> => {
      const error = await form.validate();
      if (!error) return true;

      await alertDialog(error.message);

      // simple mode should validate completely before saving
      if (simpleMode) return false;

      const confirmSaving = await confirmDialog('Der Vertrag beinhaltet Fehler. Möchten Sie trotzdem fortfahren?', {
        highlightDecline: true,
        declineText: 'Abbrechen',
        acceptText: 'Fortfahren',
      });

      return confirmSaving;
    };

    // the selected contract is new and needs to be created
    if (!selectedContract?._id) {
      const error = await form.validate();
      if (error) {
        await alertDialog(error.message);
        if (simpleMode) {
          setSaveInProcess(false);
          return;
        }
        const confirmSaving = await confirmDialog('Der Vertrag beinhaltet Fehler. Möchten Sie trotzdem fortfahren?', {
          highlightDecline: true,
          declineText: 'Abbrechen',
          acceptText: 'Fortfahren',
        });
        if (!confirmSaving) {
          setSaveInProcess(false);
          return;
        }

        // in the creation case with errors we skip the justification dialog and just create the contract
        await guardDialog(async () => {
          if (!selectedContract) return;
          await saveOrCreateEntityWithoutValidation(selectedContract, changeSupplement);
          await UserAPI.createPersonalNr(props.personID ?? '')();
        });
        await cleanup();
        setSaveInProcess(false);
        return;
      }

      if (simpleMode) {
        try {
          const { justification, comment } = await askForJustification();

          await guardDialog(async () => {
            if (!selectedContract) return;

            selectedContract.justification = justification;

            // the simple mode should create this entity now without validation as we cannot give validation options to the create function
            const _id = await saveOrCreateEntityWithoutValidation(selectedContract, changeSupplement);
            await ContractAPI.notifyMAV(_id, comment)();

            await UserAPI.createPersonalNr(props.personID ?? '')();
          });
        } catch (e) {
          skipClean = true;
          console.error(e);
        }
      } else {
        await guardDialog(async () => {
          if (!selectedContract) return;

          await saveOrCreateEntityWithoutValidation(selectedContract, changeSupplement);

          await UserAPI.createPersonalNr(props.personID ?? '')();
        });
      }
    } else if (overrideShowEmptyNewSupplement) {
      // path for manully created empty supplement. These supplements are only used as placeholders, because processes are not completly clean in the real world
      // the simple mode is not regarded here as this can only happen when a psb pressend the "new supplement" button. Thus the simple mode cannot be active here
      if (!(await validateWithConfirm())) {
        setSaveInProcess(false);
        return;
      }

      await guardDialog(async () => {
        if (!overrideShowEmptyNewSupplement) return;
        if (overrideShowEmptyNewSupplement._id) {
          await overrideShowEmptyNewSupplement.save();
        } else {
          await overrideShowEmptyNewSupplement.create();
        }
      });
    } else {
      // if the process of the inital contract is not finished we save directly to it and reopen the contract
      const meta = await fetchContractMeta(selectedContract._id);
      if (!meta) throw new Error('meta is null');

      if (
        simpleMode &&
        !appliedContract.agInfos?.unterzeichnungsdatum &&
        !selectedSupplement &&
        meta.mavStatus === MAVStatus.MAV_ABGELEHNT &&
        changeSupplement
      ) {
        if (!(await validateWithConfirm())) {
          setSaveInProcess(false);
          return;
        }

        if (
          !(await confirmDialog(
            'Sollen die Änderungen wirklich übernommen werden? Der Entwurf wird danach nochmal der MAV vorgelegt.',
            {
              declineText: 'Abbrechen',
              acceptText: 'Übernehmen und der MAV vorlegen',
            },
          ))
        ) {
          setSaveInProcess(false);
          return;
        }

        const comment = await promptDialog('Kommentar für die MAV kann auch leer bleiben.', {
          placeholder: 'Kommentar',
        });

        await guardDialog(async () => {
          if (!selectedContract._id || !changeSupplement) return;

          // write the changes of the schulleitung to the base of the contract
          await ContractAPI.saveSupplementToBase(selectedContract._id, changeSupplement)();

          // notify the mav and set the meta with a new comment
          meta.comments?.push(
            new ContractMetaInformationComment({
              userRole: Role.SCHULLEITER,
              type: ContractMetaInformationCommentType.SUPPLEMENT_REOPENED,
              text: comment,
              userID: user?._id,
            }),
          );

          for (const c of meta.comments ?? []) {
            c.oldProcess = false;
          }

          meta.mavStatus = MAVStatus.MAV_BETEILIGEN;

          await meta.save();

          await ContractAPI.notifyChange(
            selectedContract._id,
            Role.MAV,
            'ein abgelehnter Neueinstellungsentwurf wurde bearbeitet.',
          )();
        });
      } else {
        // supplement creation under normal conditions
        const hasAG1 = (selectedSupplement ?? changeSupplement)?.changes?.find(c =>
          c.path.includes('agEingruppierung1'),
        );

        const hasAG2 = (selectedSupplement ?? changeSupplement)?.changes?.find(c =>
          c.path.includes('agEingruppierung2'),
        );

        // check if the change is made on a temporary supplement which creates ag2
        const isChangeMadeOnTemporarySupplement = checkIfChangeIsMadeOnTemporarySupplement(
          appliedContract,
          selectedDate,
        );

        // if yes we have the creating supplemement here and want to copy all its changes to our supplement so we do not loose them
        // we also copy only changes we not made ourselves
        if (
          isChangeMadeOnTemporarySupplement &&
          hasAG2 &&
          (!selectedSupplement || selectedSupplement._id !== isChangeMadeOnTemporarySupplement._id)
        ) {
          for (const change of isChangeMadeOnTemporarySupplement.changes ?? []) {
            if (
              change.path.includes('agEingruppierung2') &&
              !changeSupplement?.changes?.some(c => c.path === change.path)
            ) {
              changeSupplement?.changes?.push(change);
            }
          }
        }

        if (simpleMode && !expertView) {
          if (hasAG1 && !supplementDates.startDateAG1) {
            await alertDialog('Bitte geben Sie das Startdatum für die Änderung auf der Eingruppierung 1 ein!');
            setSaveInProcess(false);
            return;
          }

          if (hasAG2 && !supplementDates.startDateAG2) {
            await alertDialog('Bitte geben Sie das Startdatum für die Änderung auf der Eingruppierung 2 ein!');
            setSaveInProcess(false);
            return;
          }

          // the intervall fields accept every date and only report the broken rule underneath themselves.
          // As they live outside of the form document, they are held to the rules here instead of by the form validator.
          const intervallDateError =
            changeStartDateError(supplementDates.startDateAG1) ??
            changeEndDateError(supplementDates.endDateAG1, supplementDates.startDateAG1) ??
            changeStartDateError(supplementDates.startDateAG2) ??
            changeEndDateError(supplementDates.endDateAG2, supplementDates.startDateAG2);
          if (intervallDateError) {
            await alertDialog(intervallDateError);
            setSaveInProcess(false);
            return;
          }
        }

        const createSupplementDialog = async () => {
          // in the simnple mode the start and enddates are on the form. these fields do not directly exist on the contract but are for the supplement. We extract them here so the user himself has a more easy flow
          if (simpleMode && !expertView) {
            const supplement = selectedSupplement ?? changeSupplement;

            if (!supplement) {
              return;
            }

            // write back the date on the supplement if a change is made in that section
            await changesContainPaths(supplement.changes, {
              generalCallback: () => {
                supplement.startDateGeneral = appliedContract.agInfos?.einstellungsdatum ?? appliedContract.createdAt;
              },
              ag1Callback: () => {
                supplement.startDateAG1 = supplementDates.startDateAG1;
                supplement.endDateAG1 = supplementDates.endDateAG1;
              },
              ag2Callback: () => {
                supplement.startDateAG2 = supplementDates.startDateAG2;
                supplement.endDateAG2 = supplementDates.endDateAG2;
              },
            });
          }

          const result = await showSupplementCreationDialog({
            simpleMode,
            contract: appliedContract,
            supplement: selectedSupplement ?? changeSupplement,
            supplementFromChange: changeSupplement,
            allSupplements: (selectedContract.supplements ?? []) as ContractSupplement[],
            kuendigungSetDate: kuendigungDate,
          });

          if (result?.supplement) {
            const newSupplement = result.supplement;
            selectedContract.supplements?.push(newSupplement);
            appliedContract.supplements?.push(newSupplement);

            // if the user is a schulleitung we now want to inform the mav of the school that a change got made. Before this would have been done by the psbs manually. Now we have all the knowledge here so we can do it
            if (simpleMode && selectedContract._id) {
              await ContractAPI.notifyMAV(selectedContract._id, result.comment, newSupplement._id)();
              setSelectedSupplement(newSupplement);
              await setApplyDateToSupplementStart(newSupplement);
              skipClean = true;
            } else {
              setSelectedSupplement(null);
            }
          }
        };

        if (!(await validateWithConfirm())) {
          setSaveInProcess(false);
          return;
        }

        await createSupplementDialog();
      }
    }

    // reset the changes and recalculate based on new things
    if (!skipClean) await cleanup();
    setSaveInProcess(false);
  };

  const getCurrentStatusMessage = () => {
    if (appliedContract && !appliedContract._id) return 'Initialer Vertragsentwurf.';
    if (selectedContract?._id && !initialProcessFinished)
      return 'Der Einstellungsprozess ist noch nicht abgeschlossen.';
    if (selectedSupplement) return `Vertragsstatus auf den ${formatCalendarDay(selectedDate, 'dd.MM.yyyy')} gesetzt!`;

    return '';
  };

  const getCurrentSimpleStatusMessage = () => {
    if (!appliedContract?._id) return 'Initialer Vertragsentwurf.';
    if (!initialProcessFinished) return 'Der Einstellungsprozess ist noch nicht abgeschlossen.';
    if (!appliedContract.agInfos?.unterzeichnungsdatum) return 'Vertrag ist nicht unterzeichnet.';
    if (selectedSupplement && !selectedSupplement.signedAt) return 'Ausgewählte Änderung ist nicht unterzeichnet.';

    return '';
  };

  if (contractsLoading || !person) {
    return html`<div class="card card-body"><div class="skeleton h-16 w-full"></div></div>`;
  }

  const supplementForEdit = (selectedSupplement ??
    supplementFromChange ??
    overrideShowEmptyNewSupplement) as Maybe<SupplementEditSupplement>;

  // without a supplement to show, the contract form gets the whole width instead of leaving a gap.
  // a contract that does not exist yet has no supplements: its changes are only the vehicle that carries the
  // entered values onto the freshly created contract, so showing them as a Nachtrag would just confuse
  const showSupplementPanel = !!supplementForEdit && !simpleMode && !!selectedContract?._id;

  const controls = html`
    <!-- sticky inside the content column so the bar never covers the sidebar; mt-auto keeps it at the bottom of a short page -->
    <div
      class="sticky bottom-0 z-50 -mx-2 mt-auto flex flex-row flex-wrap items-center justify-between gap-2 p-4 border-t border-base-300 bg-base-100/80 shadow-[0_-8px_24px_-12px_rgb(16_42_49/0.18)] backdrop-blur-md backdrop-saturate-150"
    >
      <div class="flex flex-row gap-2">
        ${
          hasEditPermission && (!simpleMode || contracts?.length === 0)
            ? html`
                <button class="btn btn-success text-white" @click=${() => handleNewContract()}>
                  <span class="icon-[material-symbols--add]"></span>
                  ${user?.roles.find(r => r.name === Role.SCHULLEITER) ? 'Einstellung' : 'Vertrag'}
                </button>
                ${
                  // a Nachtrag needs a contract to hang off, so it is only offered once the contract exists
                  simpleMode || !selectedContract?._id
                    ? nothing
                    : html`
                        <button
                          class="btn btn-success text-white"
                          @click=${() => {
                            setSelectedSupplement(null);
                            const nextNachtragNumber = (appliedContract?.supplements?.at(-1)?.nachtragsNummer ?? 0) + 1;
                            setOverrideShowEmptyNewSupplement(
                              new ContractSupplement({
                                nachtragsNummer: nextNachtragNumber,
                                vertragID: selectedContract?._id,
                              }),
                            );
                          }}
                        >
                          <span class="icon-[material-symbols--add]"></span>
                          Nachtrag
                        </button>
                      `
                }
              `
            : nothing
        }
      </div>

      <div class="flex flex-row flex-wrap items-center gap-2">
        ${
          user?.roles.find(r => r.name === Role.ADMIN) ||
          user?.roles.find(r => r.name === Role.PSB && r.contexts.includes(GLOBAL_CONTEXT))
            ? ToggleInput({
                value: expertView,
                label: 'Expertenansicht',
                onPicked: (value: boolean | null) => setExpertView(!!value),
              })
            : nothing
        }
        ${
          user?.roles.find(r => r.name === Role.PSB || r.name === Role.ADMIN) &&
          selectedContract?._id &&
          isInitialProcessMAVReady(contractMeta)
            ? html`<button class="btn btn-outline" @click=${() => handleMavBeteiligen()}>MAV beteiligen</button>`
            : nothing
        }
        ${
          canEditSupplement
            ? html`<button class="btn btn-outline btn-error" @click=${() => skipMav()}>MAV überspringen</button>`
            : nothing
        }
        ${
          selectedContract?._id || selectedSupplement?._id
            ? html`
                <button
                  class="btn btn-outline"
                  @click=${async () => {
                    await showContractDocumentManagerDialog({
                      contract: appliedContract,
                      supplement: selectedSupplement,
                    });
                  }}
                >
                  <span class="icon-[material-symbols--download]"></span>
                  Dokumente
                </button>
              `
            : nothing
        }
        ${
          supplementFromChange?.changes?.length || selectedSupplement?.isDirty() || overrideShowEmptyNewSupplement
            ? html`
                <button
                  class="btn btn-outline"
                  @click=${() => {
                    appliedContract?.revert();
                    selectedSupplement?.revert();
                    setOverrideShowEmptyNewSupplement(null);
                    setSupplementFromChange(null);
                    if (selectedContract) void recalculateAppliedContract(selectedContract, selectedDate);
                  }}
                >
                  <span class="icon-[material-symbols--history]"></span>
                  Zurücksetzen
                </button>
              `
            : nothing
        }
        ${
          selectedContract &&
          ((appliedContract && supplementFromChange) ||
            !selectedContract._id ||
            selectedSupplement?.isDirty() ||
            overrideShowEmptyNewSupplement)
            ? html`
                <button class="btn btn-primary" ?disabled=${saveInProcess} @click=${() => handleSave()}>
                  ${
                    saveInProcess || contractsLoading
                      ? html`<span class="loading loading-spinner loading-xs"></span>`
                      : html`<span class="icon-[material-symbols--save-outline]"></span>`
                  }
                  Speichern
                </button>
              `
            : nothing
        }
      </div>
    </div>
  `;

  return html`
    <!-- no bottom padding: the sticky action bar closes the page flush with the window edge -->
    <div class="flex grow flex-col gap-4 p-2 pb-0">
      ${ContractTimeline({
        message: getCurrentStatusMessage(),
        loading: contractsLoading,
        refreshing,
        contracts: (contracts ?? null) as Maybe<TimelineContract[]>,
        selectedDate,
        selectedContract: selectedContract as Maybe<TimelineContract>,
        selectedSupplement: selectedSupplement as Maybe<TimelineSupplement>,
        isEditingSupplement,
        simpleMode,
        onContractSelected: async contract => {
          if (contract && contract._id === selectedContract?._id) return;
          setIsEditingSupplement(false);
          setSelectedContract(contract as unknown as Contract | null);
          setSelectedSupplement(null);
          // the point in time belongs to the contract that was shown, so a new contract starts at today again
          setSelectedDate(todayAsCalendarDay());
          setReadonly(false);
          if (contract) await recalculateAppliedContract(contract as unknown as Contract, todayAsCalendarDay());
        },
        onDateSelected: async rawDate => {
          // the view works on canonical days, an instant with a time of day would never equal "today"
          const date = toCalendarDay(rawDate);
          if (date.getTime() === toCalendarDay(selectedDate).getTime()) {
            return;
          }
          setIsEditingSupplement(false);

          setReadonly(date.getTime() !== todayAsCalendarDay().getTime());
          setSelectedDate(date);

          // revert the changes if the date changes. As the base of the change changed
          appliedContract?.revert();

          if (selectedContract) await recalculateAppliedContract(selectedContract, date);
        },
        onSupplementSelected: async supplement => {
          setIsEditingSupplement(false);
          if (!supplement) {
            setSelectedDate(todayAsCalendarDay());
            setSelectedSupplement(null);
            if (selectedContract) await recalculateAppliedContract(selectedContract, todayAsCalendarDay());
            return;
          }
          if (supplement._id === selectedSupplement?._id) return;
          setSelectedSupplement(supplement as unknown as ContractSupplement);

          // revert the changes if the date changes. As the base of the change changed
          appliedContract?.revert();

          // set the date to the smallest of the selected supplement so we have the latest information applied
          await setApplyDateToSupplementStart(supplement);
        },
        onEditingSupplementStopped: () => setIsEditingSupplement(false),
      })}

      <div
        class="flex flex-col gap-2 lg:items-start ${
          showSupplementPanel ? 'lg:grid lg:grid-cols-[minmax(0,1fr)_26rem] xl:grid-cols-[minmax(0,1fr)_32rem]' : ''
        } ${!selectedContract ? 'grow' : ''}"
      >
        ${
          !selectedContract
            ? html`
                <!-- takes the free height on the page background, so the action bar stays at the bottom -->
                <div class="flex w-full grow items-center justify-center">
                  ${EmptyState({
                    icon: 'icon-[material-symbols--contract-rounded]',
                    title: !contracts?.length
                      ? user?.roles.find(r => r.name === Role.SCHULLEITER)
                        ? 'Keine Einstellung'
                        : 'Keine Verträge eingetragen.'
                      : 'Kein Vertrag ausgewählt.',
                    description: contracts?.length ? 'Wählen Sie oben in der Timeline einen Vertrag aus.' : undefined,
                  })}
                </div>
              `
            : html`
                <div class="w-full min-w-0">
                  ${
                    !expertView
                      ? ContractEditSimple({
                          form,
                          contract: appliedContract,
                          person: person as Person,
                          message: getCurrentSimpleStatusMessage(),
                          hasEditPermission: effectiveEditPermission,
                          canEdit: formCanEdit,
                          isInitialProcessFinished: initialProcessFinished,
                          supplementDates,
                          onSupplementDatesChanged: setSupplementDates,
                          onKuendigungSet: date => void handleKuendigungSet(date),
                          onChanged: () => void recomputeSupplementFromChange(),
                        })
                      : ContractEdit({
                          form,
                          contract: appliedContract,
                          hasEditPermission: internalLoading ? false : hasEditPermission,
                          canEdit: formCanEdit,
                          onKuendigungSet: date => void handleKuendigungSet(date),
                          onChanged: () => void recomputeSupplementFromChange(),
                        })
                  }
                </div>
                ${
                  showSupplementPanel
                    ? html`
                        <div class="min-w-0 lg:sticky lg:top-2 lg:max-h-[calc(100dvh-7rem)] lg:overflow-y-auto lg:pb-4">
                          ${SupplementEdit({
                            supplement: supplementForEdit,
                            additionalChanges: supplementFromChange?.changes,
                            possibleContractEndDate: selectedContract.agInfos?.gekuendigtZum,
                            onUpdateOuter: () => void recomputeSupplementFromChange(),
                            onEditToggled: async editing => {
                              setIsEditingSupplement(editing);
                              if (!selectedSupplement || !selectedContract) return;

                              // revert the changes if the date changes. As the base of the change changed
                              appliedContract?.revert();

                              // while editing, the drafts of the edited supplement have to be part of the applied state
                              await recalculateAppliedContract(selectedContract, selectedDate, {
                                includeDrafts: editing,
                                editingSupplement: editing,
                              });
                            },
                            onDelete: async () => {
                              if (
                                !(await confirmDialog(
                                  'Möchten Sie den Nachtrag wirklich löschen? Diese Aktion ist nicht wiederherstellbar!',
                                  { highlightDecline: true, declineText: 'Abbrechen', acceptText: 'Löschen' },
                                ))
                              )
                                return;

                              const index = selectedContract.supplements?.findIndex(
                                s => s._id === selectedSupplement?._id,
                              );
                              if (isNullish(index) || index < 0) return;

                              selectedContract.supplements?.splice(index, 1);
                              appliedContract?.supplements?.splice(index, 1);

                              void guardDialog(() => selectedSupplement!.remove(), {
                                success: () => void SgSnackbar.show('Nachtrag gelöscht!'),
                              });
                              setSelectedSupplement(null);
                            },
                          })}
                        </div>
                      `
                    : nothing
                }
              `
        }
      </div>

      ${ContractProcessComments({
        contractID: selectedContract?._id,
        onContractRemoved: () => {
          setSelectedContract(null);
          setSelectedSupplement(null);
        },
      })}
      ${hasEditPermission ? controls : nothing}
    </div>
  `;
});
